import React, { createContext, useState, useEffect, useContext } from 'react';
import { ContextStatus, UserCompanyType } from '@leedpulse-monorepo/enums';
import {
  doc,
  getDoc,
  DocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { FirestoreCollections, firestore } from '../../../firebase.config';
import AuthenticationContext from '../auth/AuthenticationContext';
import UserDoc from '../../../domain/models/UserDoc';
import getUsersByCompanyId from '../../../domain/use_cases/users/getUsersByCompanyId';
import searchCompanyUsers from '../../../domain/use_cases/users/searchCompanyUsers';
import getCompaniesBySubscriptionId from '../../../domain/use_cases/companies/getCompaniesBySubscriptionId';
import { getSubscriptionInfo } from '../../../domain/use_cases/stripe/getSubscriptionInfo';
import { ICompany } from '@leedpulse-monorepo/interfaces';

const STORAGE_KEY = 'leedflow-company-id';

export type TRoleCheckedCompany = ICompany & { isSupervisor: boolean };

interface CompanyContextValue {
  companies: TRoleCheckedCompany[];
  activeCompany: TRoleCheckedCompany | null;
  users: UserDoc[];
  usersCount: number;
  usersStatus: ContextStatus;
  status: ContextStatus;
  /** Whether the active company has a valid payment method on Stripe */
  hasValidPayment: boolean;
  /** Whether the payment method check is still loading */
  hasValidPaymentLoading: boolean;
  setActiveCompany: (_: string) => void;
  refreshUsers: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextValue>({
  companies: [],
  activeCompany: null,
  users: [],
  usersCount: 0,
  usersStatus: ContextStatus.IDLE,
  status: ContextStatus.IDLE,
  hasValidPayment: false,
  hasValidPaymentLoading: false,
  setActiveCompany: (_: string) => undefined,
  refreshUsers: async () => undefined,
});

export const CompanyProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { userDoc, authentication, getUserCompanyRole } = useContext(AuthenticationContext);
  const [companies, setCompanies] = useState<TRoleCheckedCompany[]>([]);
  const [activeCompany, setActiveCompanyState] = useState<TRoleCheckedCompany | null>(null);
  const [users, setUsers] = useState<UserDoc[]>([]);
  const [usersCount, setUsersCount] = useState<number>(0);
  const [usersStatus, setUsersStatus] = useState<ContextStatus>(ContextStatus.IDLE);
  const [status, setStatus] = useState<ContextStatus>(ContextStatus.IDLE);
  const [hasValidPayment, setHasValidPayment] = useState<boolean>(false);
  const [hasValidPaymentLoading, setHasValidPaymentLoading] = useState<boolean>(false);

  const fetchUsers = async (companyId: string) => {
    setUsersStatus(ContextStatus.LOADING);
    try {
      const fetchedUsers = await getUsersByCompanyId(companyId);
      setUsers(fetchedUsers);
      try {
        const { nbHits } = await searchCompanyUsers({ companyId, hitsPerPage: 0 }, authentication);
        setUsersCount(nbHits);
      } catch {
        setUsersCount(fetchedUsers.length); // as a backup if algolia doesnt work
      }
      setUsersStatus(ContextStatus.SUCCESS);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      setUsersCount(0);
      setUsersStatus(ContextStatus.ERROR);
    }
  };

  const refreshUsers = async () => {
    if (activeCompany) {
      await fetchUsers(activeCompany.id);
    }
  };

  const setActiveCompany = (companyId: string) => {
    const company = companies.find((company) => company.id === companyId);
    if (company) {
      setActiveCompanyState({ ...company, isSupervisor: getUserCompanyRole(company.id) === UserCompanyType.SUPERVISOR });
      localStorage.setItem(STORAGE_KEY, companyId);
    }
  };

  // Fetch users when active company changes
  useEffect(() => {
    if (!activeCompany) {
      setUsers([]);
      setUsersCount(0);
      setUsersStatus(ContextStatus.IDLE);
      return;
    }

    fetchUsers(activeCompany.id);
    // Depend on the id, not the whole object: `activeCompany` gets a new reference
    // with the same id whenever nextBillingDate resolves below, which must not
    // re-trigger a full users refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id]);

  // Fetch payment method status when active company changes
  useEffect(() => {
    if (!activeCompany || !authentication || !activeCompany.api_key) {
      setHasValidPayment(false);
      return;
    }

    let cancelled = false;
    setHasValidPaymentLoading(true);

    getSubscriptionInfo(activeCompany.id, activeCompany.api_key, authentication)
      .then((info) => {
        if (!cancelled) {
          setHasValidPayment(info.has_valid_payment ?? false);
        }
      })
      .catch((err) => {
        console.error('Error fetching payment method status:', err);
        if (!cancelled) setHasValidPayment(false);
      })
      .finally(() => {
        if (!cancelled) setHasValidPaymentLoading(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany, authentication]);

  // Fetch subscriptions when userDoc changes
  useEffect(() => {
    const userDocCompaniesIds = Object.keys(userDoc?.idCompanies || {});
    const fetchCompanies = async () => {
      if (!userDoc) {
        setCompanies([]);
        setActiveCompanyState(null);
        setStatus(ContextStatus.SUCCESS);
        return;
      }

      setStatus(ContextStatus.LOADING);

      try {
        // Fetch all subscriptions in parallel
        const companiesPromises = userDocCompaniesIds.map((companyId: string) =>
          getDoc(doc(firestore, FirestoreCollections.companies, companyId))
        );

        let fetchedCompanies: ICompany[] = [];
        if (companiesPromises.length > 0) {
          // Use allSettled so a permission-denied on a missing/deleted company
          // doesn't block the others from loading.
          const companiesResults = await Promise.allSettled(companiesPromises);
          const companiesDocs = companiesResults
            .filter((r): r is PromiseFulfilledResult<DocumentSnapshot<DocumentData>> => r.status === 'fulfilled')
            .map((r) => r.value);

          // Convert documents to Subscription objects
          fetchedCompanies = companiesDocs
            .filter((docSnapshot: DocumentSnapshot<DocumentData>) => docSnapshot.exists())
            .map((docSnapshot: DocumentSnapshot<DocumentData>) =>
              docSnapshot.data() as ICompany
            );
        } else {
          const companiesPromises = Object.keys(userDoc.idSubscription).map((subscriptionId: string) =>
            getCompaniesBySubscriptionId(subscriptionId)
          );
          fetchedCompanies = (await Promise.all(companiesPromises)).flat();
        }

        setCompanies(fetchedCompanies.map((company) => ({ ...company, isSupervisor: getUserCompanyRole(company.id) === UserCompanyType.SUPERVISOR } as TRoleCheckedCompany)));

        // Determine which subscription to set as active
        const storedCompanyId = localStorage.getItem(STORAGE_KEY);
        let companyToActivate: ICompany | null = null;

        if (storedCompanyId) {
          // Try to find the stored subscription in the fetched list
          companyToActivate =
            fetchedCompanies.find((company) => company.id === storedCompanyId) || null;
        }

        // If no stored subscription or it's not valid, use the first one
        if (!companyToActivate && fetchedCompanies.length > 0) {
          companyToActivate = fetchedCompanies[0];
          localStorage.setItem(STORAGE_KEY, companyToActivate.id);
        }

        setActiveCompanyState({ ...companyToActivate, isSupervisor: getUserCompanyRole(companyToActivate?.id || '') === UserCompanyType.SUPERVISOR } as TRoleCheckedCompany);
        setStatus(ContextStatus.SUCCESS);
      } catch (error) {
        console.error('Error fetching companies:', error);
        setStatus(ContextStatus.ERROR);
      }
    };

    fetchCompanies();
  }, [userDoc]);

  return (
    <CompanyContext.Provider
      value={{
        companies,
        activeCompany,
        users,
        usersCount,
        usersStatus,
        status,
        hasValidPayment,
        hasValidPaymentLoading,
        setActiveCompany,
        refreshUsers,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;
